import {
  array,
  InferInput,
  InferOutput,
  number,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  ValiError,
} from 'valibot';
import { PlaylistItemSchema, SortSchema } from '../apiSchema.ts';
import { getOffsetNumber } from '../utils.ts';

/**
 * Schema for the parameters of the `/v1/me/library/playlists` endpoint.
 */
export const MeLibraryPlaylistsRequestArgsSchema = object({
  limit: number(),
  offset: optional(number()),
  sort: optional(
    array(SortSchema),
  ),
});

/**
 * Type for the parameters of the `/v1/me/library/playlists` endpoint.
 */
export type MeLibraryPlaylistsRequestArgs = InferOutput<
  typeof MeLibraryPlaylistsRequestArgsSchema
>;

/**
 * Schema for the response of the `/v1/me/library/playlists` endpoint.
 */
export const MeLibraryPlaylistsResponseSchema = pipe(
  object({
    data: array(PlaylistItemSchema),
    next: optional(string()),
    meta: object({
      total: number(),
    }),
  }),
  transform(({ data, meta, next }) => {
    if (!next) {
      return {
        playlists: data,
        total: meta.total,
      };
    }

    return {
      next: getOffsetNumber(next),
      playlists: data,
      total: meta.total,
    };
  }),
);

/**
 * Type for the original response of the `/v1/me/library/playlists` endpoint.
 */
export type MeLibraryPlaylistsResponse = InferInput<
  typeof MeLibraryPlaylistsResponseSchema
>;

/**
 * Type for the normalized response data of the `/v1/me/library/playlists` endpoint.
 */
export type MeLibraryPlaylistsData = InferOutput<
  typeof MeLibraryPlaylistsResponseSchema
>;

/**
 * Schema for the player.api.music() return value of the `/v1/me/library/playlists` endpoint.
 */
export const MeLibraryPlaylistsAPISchema = object({
  data: MeLibraryPlaylistsResponseSchema,
});

/**
 * Type for the player.api.music() return value of the `/v1/me/library/playlists` endpoint.
 */
export type MeLibraryPlaylistsAPI = InferOutput<
  typeof MeLibraryPlaylistsAPISchema
>;

/**
 * Fetches the user's library playlists from the Apple Music API.
 * @param {MusicKit.MusicKitInstance} player The MusicKit instance.
 * @param {MeLibraryPlaylistsRequestArgs} params The parameters for the request.
 * @returns {Promise<MeLibraryPlaylistsData>} The normalized playlists data.
 */
export const $getMeLibraryPlaylists = async (
  player: MusicKit.MusicKitInstance,
  params: MeLibraryPlaylistsRequestArgs,
): Promise<MeLibraryPlaylistsData> => {
  const res = await player.api.music(
    '/v1/me/library/playlists',
    params,
  ) as unknown as MeLibraryPlaylistsAPI;

  const parsed = safeParse(MeLibraryPlaylistsResponseSchema, res.data);
  if (!parsed.success) {
    console.error(
      '[/v1/me/library/playlists] Validation failed:',
      JSON.stringify(parsed.issues, null, 2),
    );
    throw new ValiError(parsed.issues);
  }
  return parsed.output;
};
